import * as Yup from 'yup';
import { SchFindTimeField } from './Fields/SchFindTimeField';
import { SchInterviewerField } from './Fields/SchInterviewerField/SchInterviewerField';
import { useSchDurationTimeFields } from './Fields/useSchDurationTimeFields';
import { useSchLocationField } from './Fields/useSchLocationField';
import { SchMeetingRoom } from './Fields/SchMeetingRoom';
import { useCallback } from 'react';
import { SchBulkCalendarInvite } from './Fields/SchBulkCalendarInvite';
import { PublicprivateSchedulingField } from './Fields/PublicPrivateSchedulingField';
import { useCalendarSchedulingFields } from './Fields/useCalendarSchedulingFields';

const INTERVIEW_TYPE_OPTIONS = [
  {
    label: 'One-on-one interview',
    value: 'one_on_one',
  },
  {
    label: 'Panel interview',
    value: 'panel',
  },
];

const MEETING_ROOM_CACHE_KEY = 'meetingRoomOptionsCache_schedular';

export const useInterviewSchedulerFormConfig = ({
  calendarRef,
  interviewDataRef,
  updateSharedData,
  registerForUpdates,
  interviewPlan,
  locationOptions,
  jobStageActivityId,
  variant,
  formRef,
  isAdhoc,
  isEdit,
  isBulkManual,
  applicationData,
  feSchedulingPreference,
  currUser,
  currentTemplateData,
  updateTemplateModificationData,
  interviewPlatform,
  showScorecardStep = false,
}) => {
  const isSelfSchedule = variant === 'self' || variant === 'bulk-self';
  const locationBasedFields =
    useSchLocationField({
      registerForUpdates,
      updateSharedData,
      interviewPlan,
      locationOptions,
      isEdit,
      formRef,
      interviewDataRef,
      isSelfSchedule,
      interviewPlatform,
      showScorecardStep,
    }) || [];
  const durationAndTimeBasedFields =
    useSchDurationTimeFields({
      updateSharedData,
      interviewDataRef,
      calendarRef,
      registerForUpdates,
      formRef,
      variant,
      isBulkManual,
    }) || [];

  const { fields: calendarField } = useCalendarSchedulingFields({
    updateSharedData,
    interviewDataRef,
    isSelfSchedule,
    isEdit,
    interviewPlan,
    isBulkManual,
    registerForUpdates,
  });
  // Conditionally use the results based on feature flags
  const hasConnectedCalendar =
    currUser?.connected_calendar?.gcal || currUser?.connected_calendar?.mscal;
  const isSharedCalendarEnabled =
    isEdit || interviewPlan?.shared_calendar_enabled;
  const shouldShowCalendarFields =
    feSchedulingPreference && hasConnectedCalendar && isSharedCalendarEnabled;

  const calendarBasedFields = shouldShowCalendarFields ? calendarField : [];
  const toShowMeetingRoom = interviewPlan?.meeting_rooms_enabled;

  // Define the wrapper component outside the config to ensure a stable reference
  const InterviewerFieldWrapper = useCallback(
    (props) => (
      <SchInterviewerField
        interviewDataRef={interviewDataRef}
        updateSharedData={updateSharedData}
        registerForUpdates={registerForUpdates}
        interviewPlan={interviewPlan}
        name="interviewers"
        variant={variant}
        isAdhoc={isAdhoc}
        editMode={isEdit}
        jobStageActivityId={jobStageActivityId}
        isBulkManual={isBulkManual}
        {...props}
      />
    ),
    [
      interviewDataRef,
      updateSharedData,
      registerForUpdates,
      interviewPlan,
      variant,
      isAdhoc,
      isEdit,
      jobStageActivityId,
      isBulkManual,
    ]
  );

  // Define a wrapper for SchMeetingRoom to pass additional props
  const MeetingRoomWrapper = useCallback(() => {
    const localCache = localStorage.getItem(MEETING_ROOM_CACHE_KEY);
    const localCacheData = localCache ? JSON.parse(localCache) : undefined;
    return (
      <SchMeetingRoom
        isMulti={false}
        updateSharedData={updateSharedData}
        registerForUpdates={registerForUpdates}
        jobStageActivityId={jobStageActivityId}
        isBulkManual={isBulkManual}
        interviewDataRef={interviewDataRef}
        cacheKey={MEETING_ROOM_CACHE_KEY}
        localCacheData={localCacheData}
        isEdit={isEdit}
      />
    );
  }, [
    updateSharedData,
    registerForUpdates,
    jobStageActivityId,
    isBulkManual,
    interviewDataRef,
    isEdit,
  ]);

  const formConfig = [
    ...calendarBasedFields,
    {
      name: 'name',
      label: 'Interview name',
      maxLength: 250,
      type: 'input',
      isRequired: true,
      placeholder: 'Add interview name here',
      customOnChange: (event) => {
        const value = event?.target?.value;
        updateSharedData('interviewName', value);
      },
    },
    {
      name: 'kind',
      label: 'Interview type',
      type: 'select',
      isRequired: true,
      placeholder: 'Select Interview Type',
      options: INTERVIEW_TYPE_OPTIONS,
      customOnChange: (option) => {
        if (option?.value) {
          updateSharedData('interviewKind', option?.value);
        }
      },
    },
    {
      type: 'custom',
      name: 'interviewers',
      label: 'Interviewer',
      isRequired: true,
      validation: Yup.lazy((value) => {
        if (Array.isArray(value)) {
          return Yup.array()
            .of(Yup.string().required('Interviewer name is required'))
            .min(1, 'At least one interviewer is required')
            .required('Array is required');
        }
        return Yup.string().required('Interviewer is required');
      }),
      Field: InterviewerFieldWrapper,
    },
    ...(toShowMeetingRoom
      ? [
          {
            name: 'meeting_rooms_enabled',
            label: 'Book a meeting room',
            type: 'checkbox',
            fieldProps: { label: 'ok' },
            isNewFilter: true,
            labelStyles: { fontSize: '14px', fontWeight: '450' },
          },
          {
            type: 'custom',
            name: 'meeting_room_ids',
            isConditional: true,
            conditionConfig: { field: 'meeting_rooms_enabled', value: true },
            Field: MeetingRoomWrapper,
          },
        ]
      : []),
    isBulkManual && {
      type: 'custom',
      name: 'bulk_calendar_invite_preview',
      Field: (props) => (
        <SchBulkCalendarInvite
          {...props}
          applicationData={applicationData}
          interviewPlan={interviewPlan}
          updateSharedData={updateSharedData}
          registerForUpdates={registerForUpdates}
          interviewDataRef={interviewDataRef}
          currentTemplateData={currentTemplateData}
          updateTemplateModificationData={updateTemplateModificationData}
        />
      ),
    },
    ...durationAndTimeBasedFields,
    {
      isConditional: true,
      conditionConfig: {
        field: 'interviewers',
        type: 'custom',
        customFunc: (_, allValues) => {
          const isAtlLeastOneInterviewer = allValues?.interviewers?.length > 0;
          return isSelfSchedule && isAtlLeastOneInterviewer;
        },
      },
      type: 'custom',
      name: 'findTime',
      Field: () => (
        <SchFindTimeField
          updateSharedData={updateSharedData}
          interviewDataRef={interviewDataRef}
          registerForUpdates={registerForUpdates}
        />
      ),
    },
    {
      name: 'publicPrivateScheduling',
      type: 'custom',
      Field: () => (
        <PublicprivateSchedulingField
          updateSharedData={updateSharedData}
          interviewPlan={interviewPlan}
        />
      ),
    },
    ...locationBasedFields,
  ];

  return {
    formConfig,
  };
};
